import React from 'react';
import {
  Shield,
  Lock,
  Eye,
  FileText,
  Mail,
  Phone,
  Calendar,
  User,
} from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = 'Janeiro de 2025';

  return (
    <section id='privacidade' className='py-40 bg-gray-50'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <div className='inline-flex items-center bg-blue-100 text-blue-600 px-6 py-3 rounded-full font-semibold mb-6'>
            <Shield className='w-5 h-5 mr-2' />
            Política de Privacidade
          </div>

          <h2 className='text-4xl lg:text-5xl font-bold mb-6'>
            Sua privacidade é nossa
            <span className='gradient-text block'>prioridade</span>
          </h2>

          <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
            No FinZiin, levamos sua privacidade muito a sério. Esta política
            explica como coletamos, usamos e protegemos seus dados pessoais.
          </p>
        </div>

        {/* Content */}
        <div className='max-w-4xl mx-auto'>
          <div className='bg-white rounded-2xl shadow-xl overflow-hidden'>
            {/* Quick Info Cards */}
            <div className='bg-gradient-to-r from-blue-500 to-emerald-500 text-white p-8'>
              <div className='grid md:grid-cols-3 gap-6'>
                <div className='text-center'>
                  <Lock className='w-8 h-8 mx-auto mb-3' />
                  <h3 className='font-bold mb-2'>Dados Criptografados</h3>
                  <p className='text-blue-100 text-sm'>
                    Criptografia de ponta para máxima segurança
                  </p>
                </div>
                <div className='text-center'>
                  <Eye className='w-8 h-8 mx-auto mb-3' />
                  <h3 className='font-bold mb-2'>Transparência Total</h3>
                  <p className='text-blue-100 text-sm'>
                    Você sabe exatamente o que coletamos
                  </p>
                </div>
                <div className='text-center'>
                  <User className='w-8 h-8 mx-auto mb-3' />
                  <h3 className='font-bold mb-2'>Seus Direitos</h3>
                  <p className='text-blue-100 text-sm'>
                    Controle total sobre seus dados
                  </p>
                </div>
              </div>
            </div>

            <div className='p-8 space-y-8'>
              {/* 1. Informações que Coletamos */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <FileText className='w-6 h-6 mr-3 text-blue-500' />
                  1. Informações que Coletamos
                </h3>

                <div className='space-y-4 text-gray-600'>
                  <div className='bg-blue-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-blue-800 mb-2'>
                      Dados de Conta:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm'>
                      <li>Nome e email para criação da conta</li>
                      <li>
                        Número de telefone (opcional, para criação da conta)
                      </li>
                      <li>Foto de perfil (opcional, para personalização)</li>
                    </ul>
                  </div>

                  <div className='bg-emerald-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-emerald-800 mb-2'>
                      Dados Financeiros:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm'>
                      <li>
                        Saldos e movimentações que você inserir manualmente
                      </li>
                      <li>Nomes de bancos e tipos de conta</li>
                      <li>Metas financeiras e limites de gastos</li>
                      <li>
                        <strong>NÃO coletamos:</strong> senhas bancárias, dados
                        de cartão ou acesso às suas contas
                      </li>
                    </ul>
                  </div>

                  <div className='bg-purple-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-purple-800 mb-2'>
                      Dados de Uso:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm'>
                      <li>
                        Como você usa o aplicativo (funcionalidades mais
                        acessadas)
                      </li>
                      <li>Dados de desempenho para melhorar a experiência</li>
                      <li>Logs de erro para correções técnicas</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 2. Como Usamos seus Dados */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Lock className='w-6 h-6 mr-3 text-emerald-500' />
                  2. Como Usamos seus Dados
                </h3>

                <div className='grid md:grid-cols-2 gap-4'>
                  <div className='bg-gray-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-gray-800 mb-3'>
                      ✅ Para que usamos:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-gray-600'>
                      <li>Fornecer os serviços do FinZiin</li>
                      <li>Sincronizar dados entre dispositivos</li>
                      <li>Personalizar sua experiência</li>
                      <li>Enviar notificações importantes</li>
                      <li>Melhorar o aplicativo</li>
                      <li>Fornecer suporte técnico</li>
                    </ul>
                  </div>

                  <div className='bg-red-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-red-800 mb-3'>
                      ❌ NUNCA fazemos:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-red-600'>
                      <li>Vender seus dados para terceiros</li>
                      <li>Compartilhar informações financeiras</li>
                      <li>Usar dados para publicidade externa</li>
                      <li>Acessar suas contas bancárias</li>
                      <li>Enviar spam ou emails não solicitados</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 3. Compartilhamento de Dados */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Shield className='w-6 h-6 mr-3 text-purple-500' />
                  3. Compartilhamento de Dados
                </h3>

                <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg'>
                  <h4 className='font-bold text-yellow-800 mb-2'>
                    🔒 Política de Não Compartilhamento
                  </h4>
                  <p className='text-yellow-700 text-sm mb-3'>
                    <strong>
                      Seus dados financeiros NUNCA são compartilhados
                    </strong>{' '}
                    com terceiros, exceto nas seguintes situações limitadas:
                  </p>
                  <ul className='list-disc list-inside space-y-1 text-sm text-yellow-700'>
                    <li>
                      <strong>Provedores técnicos:</strong> Google Cloud
                      (backup), Firebase (autenticação) - sob contratos
                      rigorosos
                    </li>
                    <li>
                      <strong>Obrigação legal:</strong> Apenas se exigido por
                      lei ou ordem judicial
                    </li>
                    <li>
                      <strong>Seu consentimento:</strong> Somente com sua
                      autorização expressa
                    </li>
                  </ul>
                </div>
              </div>

              {/* 4. Seus Direitos (LGPD) */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <User className='w-6 h-6 mr-3 text-green-500' />
                  4. Seus Direitos (LGPD)
                </h3>

                <div className='bg-green-50 p-6 rounded-lg'>
                  <p className='text-green-800 mb-4 font-medium'>
                    Conforme a Lei Geral de Proteção de Dados (LGPD), você tem
                    os seguintes direitos:
                  </p>

                  <div className='grid md:grid-cols-2 gap-4'>
                    <div>
                      <h4 className='font-semibold text-green-800 mb-2'>
                        Direitos de Acesso:
                      </h4>
                      <ul className='list-disc list-inside space-y-1 text-sm text-green-700'>
                        <li>Confirmar se processamos seus dados</li>
                        <li>Acessar seus dados pessoais</li>
                        <li>Corrigir dados incompletos ou incorretos</li>
                        <li>Solicitar portabilidade dos dados</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className='font-semibold text-green-800 mb-2'>
                        Direitos de Controle:
                      </h4>
                      <ul className='list-disc list-inside space-y-1 text-sm text-green-700'>
                        <li>Excluir dados desnecessários ou excessivos</li>
                        <li>Revogar consentimento</li>
                        <li>Opor-se ao tratamento de dados</li>
                        <li>Solicitar revisão de decisões automatizadas</li>
                      </ul>
                    </div>
                  </div>

                  <div className='mt-4 p-3 bg-green-100 rounded-lg'>
                    <p className='text-green-800 text-sm'>
                      <strong>Para exercer seus direitos:</strong> Entre em
                      contato através do email
                      <strong> fin​ziin.app@gmail.com</strong> ou pela seção
                      "Segurança de Dados" do site.
                    </p>
                  </div>
                </div>
              </div>

              {/* 5. Retenção de Dados */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Calendar className='w-6 h-6 mr-3 text-orange-500' />
                  5. Retenção de Dados
                </h3>

                <div className='space-y-3'>
                  <div className='flex items-start space-x-3'>
                    <div className='bg-blue-100 p-2 rounded-full mt-1'>
                      <User className='w-4 h-4 text-blue-600' />
                    </div>
                    <div>
                      <h4 className='font-semibold text-gray-800'>
                        Dados de Conta:
                      </h4>
                      <p className='text-gray-600 text-sm'>
                        Mantidos enquanto sua conta estiver ativa ou conforme
                        necessário para fornecer serviços.
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3'>
                    <div className='bg-emerald-100 p-2 rounded-full mt-1'>
                      <FileText className='w-4 h-4 text-emerald-600' />
                    </div>
                    <div>
                      <h4 className='font-semibold text-gray-800'>
                        Dados Financeiros:
                      </h4>
                      <p className='text-gray-600 text-sm'>
                        Excluídos imediatamente após solicitação ou fechamento
                        da conta, salvo obrigações legais.
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3'>
                    <div className='bg-purple-100 p-2 rounded-full mt-1'>
                      <Lock className='w-4 h-4 text-purple-600' />
                    </div>
                    <div>
                      <h4 className='font-semibold text-gray-800'>
                        Logs Técnicos:
                      </h4>
                      <p className='text-gray-600 text-sm'>
                        Mantidos por até 90 dias para fins de segurança e
                        melhoria do serviço.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Contato */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Mail className='w-6 h-6 mr-3 text-blue-500' />
                  6. Entre em Contato
                </h3>

                <div className='bg-blue-50 p-6 rounded-lg'>
                  <p className='text-blue-800 mb-4'>
                    Tem dúvidas sobre esta política ou quer exercer seus
                    direitos? Entre em contato conosco:
                  </p>

                  <div className='grid md:grid-cols-2 gap-4'>
                    <div className='flex items-center space-x-3'>
                      <Mail className='w-5 h-5 text-blue-600' />
                      <div>
                        <div className='font-semibold text-blue-800'>
                          Email de Privacidade:
                        </div>
                        <div className='text-blue-600'>
                          fin​ziin.app@gmail.com
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center space-x-3'>
                      <Phone className='w-5 h-5 text-blue-600' />
                      <div>
                        <div className='font-semibold text-blue-800'>
                          Suporte Geral:
                        </div>
                        <div className='text-blue-600'>
                          fin​ziin.app@gmail.com
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='mt-4 pt-4 border-t border-blue-200'>
                    <p className='text-blue-700 text-sm'>
                      <strong>Tempo de resposta:</strong> Respondemos todas as
                      solicitações relacionadas à privacidade em até 72 horas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className='text-center mt-16'>
          <div className='bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-2xl p-8 max-w-2xl mx-auto'>
            <h3 className='text-2xl font-bold mb-4'>
              Seus dados estão seguros conosco
            </h3>
            <p className='text-blue-100 mb-6'>
              Baixe o FinZiin e tenha a tranquilidade de usar um app que
              respeita sua privacidade.
            </p>
            <a
              href='https://play.google.com/store/apps/details?id=br.android.finziin'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center bg-white text-blue-600 font-bold py-3 px-6 rounded-xl hover:bg-gray-100 transition-colors duration-300'
            >
              <Shield className='w-5 h-5 mr-2' />
              Baixar com Segurança
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
